import type {
	CmdbObjectTypeAttributeDefault,
	CmdbObjectTypeAttributeGroup,
	CmdbObjectTypeAttributeObject,
	CmdbObjectTypeAttributeStatus,
	CmdbObjectTypeAttributeUser,
	CmdbObjectTypeAttributeProject,
	CmdbObjectTypeAttributeBitbucketRepo,
	AttributeTypeMap,
	DefaultAttributeSubtypeMap,
	CmdbObjectTypeAttributeOpsgenieTeam,
} from './attribute-types.tsx';
import type {
	CmdbObjectAttributeValueDefault,
	CmdbObjectAttributeValueGroup,
	CmdbObjectAttributeValueProject,
	CmdbObjectAttributeValueObject,
	CmdbObjectAttributeValueStatus,
	CmdbObjectAttributeValueUser,
	CmdbObjectAttributeValueBitbucketRepo,
	CmdbObjectAttributeValueOpsgenieTeam,
} from './attribute-values.tsx';
import type { ObjectTypeAttributeId } from './shared-types/index.tsx';

export type CmdbObjectAttributeBase<T, TA, AV> = {
	objectTypeAttributeId: ObjectTypeAttributeId;
	type: T;
	objectTypeAttribute: TA;
	objectAttributeValues: AV;
};

export type NumericAttributeType = (typeof AttributeTypeMap)[keyof typeof AttributeTypeMap];
export type NumericDefaultAttributeType =
	(typeof DefaultAttributeSubtypeMap)[keyof typeof DefaultAttributeSubtypeMap];

export type CmdbObjectAttributeDefault = CmdbObjectAttributeBase<
	0,
	CmdbObjectTypeAttributeDefault,
	CmdbObjectAttributeValueDefault[]
>;

export type CmdbObjectAttributeObject = CmdbObjectAttributeBase<
	1,
	CmdbObjectTypeAttributeObject,
	CmdbObjectAttributeValueObject[]
>;

export type CmdbObjectAttributeUser = CmdbObjectAttributeBase<
	2,
	CmdbObjectTypeAttributeUser,
	CmdbObjectAttributeValueUser[]
>;

export type CmdbObjectAttributeGroup = CmdbObjectAttributeBase<
	4,
	CmdbObjectTypeAttributeGroup,
	CmdbObjectAttributeValueGroup[]
>;

export type CmdbObjectAttributeProject = CmdbObjectAttributeBase<
	6,
	CmdbObjectTypeAttributeProject,
	CmdbObjectAttributeValueProject[]
>;

export type CmdbObjectAttributeStatus = CmdbObjectAttributeBase<
	7,
	CmdbObjectTypeAttributeStatus,
	CmdbObjectAttributeValueStatus[]
>;

export type CmdbObjectAttributeBitbucketRepo = CmdbObjectAttributeBase<
	8,
	CmdbObjectTypeAttributeBitbucketRepo,
	CmdbObjectAttributeValueBitbucketRepo[]
>;

export type CmdbObjectAttributeOpsgenieTeam = CmdbObjectAttributeBase<
	9,
	CmdbObjectTypeAttributeOpsgenieTeam,
	CmdbObjectAttributeValueOpsgenieTeam[]
>;

export type CmdbObjectAttribute =
	| CmdbObjectAttributeDefault
	| CmdbObjectAttributeObject
	| CmdbObjectAttributeUser
	| CmdbObjectAttributeGroup
	| CmdbObjectAttributeProject
	| CmdbObjectAttributeStatus
	| CmdbObjectAttributeBitbucketRepo
	| CmdbObjectAttributeOpsgenieTeam;

/**
 * Used to manually refine the object attribute types
 */
export const toObjectTypeAttribute = (attr: CmdbObjectAttribute): CmdbObjectAttribute => {
	switch (attr.type) {
		case 0:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return attr as CmdbObjectAttributeDefault;
		case 1:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return attr as CmdbObjectAttributeObject;
		case 2:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return attr as CmdbObjectAttributeUser;
		case 4:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return attr as CmdbObjectAttributeGroup;
		case 6:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return attr as CmdbObjectAttributeProject;
		case 7:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return attr as CmdbObjectAttributeStatus;
		case 8:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return attr as CmdbObjectAttributeBitbucketRepo;
		case 9:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return attr as CmdbObjectAttributeOpsgenieTeam;
		default:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return attr as CmdbObjectAttributeDefault;
	}
};
